import React, { useState, useRef, useEffect } from 'react';
import { Editor, EditorState, CompositeDecorator, ContentState, Modifier, EditorBlock } from 'draft-js';
import createPrismDecorator from 'draft-js-prism-decorator';
import { useTranslation } from 'react-i18next';
import OpenInNew from '@material-ui/icons/OpenInNew';
import EditorDialog from './EditorDialog';
import './CodeEditor.css';
/**
 * @Description This function lets you return component according to the name of the incoming action.
 * @Param       action - object
 * @Param       inputName - string
 * @Param       variables - object variable list
 * @Param       multiline - boolean
 * @Param       include - array
 * @Param       disabled - boolean
 * @Param       setOpenVariable - open variable function that allows us to open
 * @param       isRequired - boolean
 * @Returns     Component
 * @Author      ibrahim AKIN
 */

const styles = {
    handle: {
        color: '#000',
        fontWeight: 'bold',
        direction: 'ltr',
        unicodeBidi: 'bidi-override'
    }
};

const HANDLE_REGEX = /(##\w*(\[+\w+\]+)*##)/g;

function handleStrategy(contentBlock, callback, contentState) {
    findWithRegex(HANDLE_REGEX, contentBlock, callback);
}

function findWithRegex(regex, contentBlock, callback) {
    const text = contentBlock.getText();
    let matchArr, start;
    while ((matchArr = regex.exec(text)) !== null) {
        start = matchArr.index;
        callback(start, start + matchArr[0].length);
    }
}
const HandleSpan = (props) => {
    return (
        <span style={styles.handle} data-offset-key={props.offsetKey}>
            {props.children}
        </span>
    );
};

const LineNumber = (props) => {
    const { block, contentState } = props;
    const lineNumber = contentState.getBlockMap().toList().findIndex(item => item.key === block.key) + 1;
    return (
        <div className="line-code" data-line-number={lineNumber}>
            <div className="line-text">
                <EditorBlock {...props} />
            </div>
        </div>
    );
};

const blockRendererFn = () => ({ component: LineNumber });

function CodeEditor({ action, inputName, variables, onAddClick, multiline, language, include, disabled, isRequired, dialog, hideRequired, out_label }) {
    const prismDecorator = createPrismDecorator({ getLanguage: () => language ? language : 'typescript' });
    const compositeDecorator = useRef(new CompositeDecorator([{
        strategy: handleStrategy,
        component: HandleSpan
    }, prismDecorator]));
    const { t } = useTranslation();
    const label = out_label ? out_label : 'CONTENT.TEXT-EDITOR.';
    const [dialogOpen, setDialog] = useState(false);
    const getValue = () => {
        return action.fields[inputName]?.value ? action.fields[inputName]?.value : '';
    };
    const setValue = (editorState) => {
        action.fields[inputName] = { value: editorState.getCurrentContent().getPlainText(), isRequired };
    };
    const createState = (value) => {
        return ({
            editorState: EditorState.createWithContent(
                ContentState.createFromText(value),
                compositeDecorator.current
            )
        });
    };
    const [state, setState] = useState(createState(getValue()));

    useEffect(() => {
        const editorState = createState(getValue());
        const initailContent = editorState.editorState.getCurrentContent();
        const lines = editorState.editorState.getSelection().merge({
            anchorKey: initailContent.getFirstBlock().getKey(),
            anchorOffset: 0,
            focusOffset: initailContent.getLastBlock().getText().length,
            focusKey: initailContent.getLastBlock().getKey()
        });
        const newState = Modifier.setBlockType(initailContent, lines, 'code-block');
        setState((oldState) => ({ editorState: EditorState.push(oldState.editorState, newState) }));
    }, [action, inputName, disabled, dialogOpen]);

    const onChange = (editorState) => {
        setValue(editorState);
        setState({ editorState });
    };
    if (onAddClick) {
        onAddClick.current = (variable) => {
            setState({ editorState: insertText('##' + variable.name + '##', state.editorState) });
        };
    }
    const handleTab = (e) => {
        e.preventDefault();
        let currentState = state.editorState;
        let newContentState = Modifier.replaceText(
            currentState.getCurrentContent(),
            currentState.getSelection(),
            '   '
        );
        setState({
            editorState: EditorState.push(currentState, newContentState, 'insert-characters')
        });
    };
    const insertText = (text, editorState) => {
        const currentContent = editorState.getCurrentContent(), currentSelection = editorState.getSelection();
        const newContent = Modifier.replaceText(currentContent, currentSelection, text);
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
        return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());
    };

    const requireValidate = !getValue() && isRequired;
    return (
        <>
            {!dialog && requireValidate && !hideRequired && <small style={{ color: '#D75E5E' }}>&nbsp;{t(label + 'REQUIRED')}</small>}
            <div className={`editable${dialog ? ' dialog' : ' code'}`}>
                {!dialog && <div className="open-editor">
                    <button disabled={disabled} onClick={() => { setDialog(true); }}>
                        <OpenInNew className="open-new" />
                        {t(label + 'OPEN')}
                    </button>
                </div>}
                <Editor editorState={state.editorState} onChange={onChange}
                    readOnly={disabled} style={{ minHeight: multiline && 100 }}
                    blockRendererFn={blockRendererFn} onTab={handleTab} />
            </div>
            {dialogOpen && <EditorDialog action={action} inputName={inputName} language={language}
                variables={variables} setOpen={setDialog} include={include} out_label={out_label} multiline isRequired />}
        </>
    );
}
export { CodeEditor };